import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X, Clock, Save, Palette, Moon, Sun, Monitor, Bell } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';


const API = "/api";

const SettingsModal = ({ isOpen, onClose }) => {
  const { user, refreshUser } = useAuth();
  const { theme: currentTheme, setTheme } = useTheme();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [autoLogoutHours, setAutoLogoutHours] = useState(8);
  const [enableAutoLogoutTime, setEnableAutoLogoutTime] = useState(false);
  const [logoutAtTime, setLogoutAtTime] = useState('17:00');
  const [enableDefaultLogoutHours, setEnableDefaultLogoutHours] = useState(true);
  const [theme, setThemeState] = useState(currentTheme || 'light');
  const [notificationHistoryLimit, setNotificationHistoryLimit] = useState("unlimited");

  useEffect(() => {
    if (isOpen && user) {
      fetchUserSettings();
    }
  }, [isOpen]); // Only re-run when modal opens/closes, not when user changes

  // Update notification limit when user data changes, but only if modal is not open
  useEffect(() => {
    if (user && !isOpen) {
      setNotificationHistoryLimit(user?.notification_history_limit === null ? "unlimited" : user?.notification_history_limit?.toString() || "unlimited");
    }
  }, [user?.notification_history_limit, isOpen]);

  const fetchUserSettings = async () => {
    try {
      // Don't refresh user data here to avoid infinite loops
      const response = await axios.get(`${API}/users/${user.id}/auto-logout-settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
        }
      });
      setAutoLogoutHours(response.data.default_auto_logout_hours || 8);
      
      // Fetch user preferences for theme and other settings
      try {
        const prefsResponse = await axios.get(`${API}/users/${user.id}/preferences`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
          }
        });
        
        const preferences = prefsResponse.data;
        setEnableAutoLogoutTime(preferences.enable_auto_logout_time || false);
        setLogoutAtTime(preferences.logout_at_time || '17:00');
        setEnableDefaultLogoutHours(preferences.enable_default_logout_hours !== false);
        const savedTheme = preferences.theme || 'light';
        setThemeState(savedTheme);
        // Only set theme if it's different from current theme to avoid flashing
        if (savedTheme !== currentTheme) {
          setTheme(savedTheme);
        }
        setNotificationHistoryLimit(user?.notification_history_limit === null ? "unlimited" : user?.notification_history_limit?.toString() || "unlimited");
      } catch (prefsError) {
        console.warn('Preferences endpoint not available, using defaults:', prefsError);
        // Use default values if preferences endpoint fails
        setEnableAutoLogoutTime(false);
        setLogoutAtTime('17:00');
        setEnableDefaultLogoutHours(true);
        setThemeState('light');
        if (currentTheme !== 'light') {
          setTheme('light');
        }
        setNotificationHistoryLimit("unlimited");
      }
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
    }
    
    // Ensure notification limit is set with the latest user data
    setNotificationHistoryLimit(user?.notification_history_limit === null ? "unlimited" : user?.notification_history_limit?.toString() || "unlimited");
  };

  const handleThemeChange = (newTheme) => {
    setThemeState(newTheme);
    // Only apply theme if it's different from current theme
    if (newTheme !== currentTheme) {
      setTheme(newTheme);
    }
  };

  const handleSaveSettings = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Save auto logout settings
      await axios.put(
        `${API}/users/${user.id}/auto-logout-settings`,
        {
          default_auto_logout_hours: enableDefaultLogoutHours ? autoLogoutHours : null
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
          }
        }
      );

      // Save notification history limit
      const limitValue = notificationHistoryLimit === "unlimited" ? null : parseInt(notificationHistoryLimit);
      console.log('Saving notification limit:', { notificationHistoryLimit, limitValue });
      
      // Update user data and refresh
      await axios.put(
        `${API}/users/${user.id}`,
        { notification_history_limit: limitValue },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
          }
        }
      );

      // Don't refresh user data here as it causes infinite loops and state overrides

      // Save user preferences (with fallback if endpoint doesn't exist)
      try {
        await axios.put(
          `${API}/users/${user.id}/preferences`,
          {
            enable_auto_logout_time: enableAutoLogoutTime,
            logout_at_time: enableAutoLogoutTime ? logoutAtTime : null,
            enable_default_logout_hours: enableDefaultLogoutHours,
            theme: theme
          },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
            }
          }
        );
      } catch (prefsError) {
        console.warn('Could not save preferences, endpoint may not be available:', prefsError);
        // Don't fail the whole save if preferences endpoint doesn't work
      }

      // Theme is already applied through handleThemeChange
      toast({
        title: "Success",
        description: "Settings saved successfully",
      });
      onClose();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                User Settings
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Configure your preferences
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Auto Logout Settings */}
          <Card className="dark:bg-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-slate-900 dark:text-slate-100">Auto Logout Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="enableDefaultLogoutHours"
                  checked={enableDefaultLogoutHours}
                  onCheckedChange={(checked) => setEnableDefaultLogoutHours(checked)}
                />
                <Label htmlFor="enableDefaultLogoutHours" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Enable Default Auto Logout Hours
                </Label>
              </div>
              
              {enableDefaultLogoutHours && (
                <div>
                  <Label htmlFor="autoLogoutHours" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Default Auto Logout Hours
                  </Label>
                  <Input
                    id="autoLogoutHours"
                    type="number"
                    min="1"
                    max="24"
                    step="0.5"
                    value={autoLogoutHours}
                    onChange={(e) => setAutoLogoutHours(parseFloat(e.target.value) || 8)}
                    className="mt-1 dark:bg-slate-600 dark:border-slate-500 dark:text-white"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    You will be automatically logged out from products after this many hours
                  </p>
                </div>
              )}
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="enableAutoLogoutTime"
                  checked={enableAutoLogoutTime}
                  onCheckedChange={(checked) => setEnableAutoLogoutTime(checked)}
                />
                <Label htmlFor="enableAutoLogoutTime" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Enable Logout at Specific Time
                </Label>
              </div>
              
              {enableAutoLogoutTime && (
                <div>
                  <Label htmlFor="logoutAtTime" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Logout Time
                  </Label>
                  <Input
                    id="logoutAtTime"
                    type="time"
                    value={logoutAtTime}
                    onChange={(e) => setLogoutAtTime(e.target.value)}
                    className="mt-1 dark:bg-slate-600 dark:border-slate-500 dark:text-white"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    You will be automatically logged out at this time every day
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Theme Settings */}
          <Card className="dark:bg-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Theme Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="theme" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Choose Theme
                </Label>
                <Select value={theme} onValueChange={handleThemeChange}>
                  <SelectTrigger className="mt-1 dark:bg-slate-600 dark:border-slate-500 dark:text-white">
                    <SelectValue placeholder="Select a theme" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light" className="flex items-center gap-2">
                      <Sun className="w-4 h-4" />
                      Light
                    </SelectItem>
                    <SelectItem value="dark" className="flex items-center gap-2">
                      <Moon className="w-4 h-4" />
                      Dark
                    </SelectItem>
                    <SelectItem value="system" className="flex items-center gap-2">
                      <Monitor className="w-4 h-4" />
                      System
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  System theme follows your device's appearance settings
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Notification Settings */}
          <Card className="dark:bg-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notification Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="notificationHistoryLimit" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Notification History Limit
                </Label>
                <Select value={notificationHistoryLimit} onValueChange={setNotificationHistoryLimit}>
                  <SelectTrigger className="mt-1 dark:bg-slate-600 dark:border-slate-500 dark:text-white">
                    <SelectValue placeholder="Select notification limit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unlimited">Keep all notifications</SelectItem>
                    <SelectItem value="0">Turn off notifications</SelectItem>
                    <SelectItem value="5">Show last 5 notifications</SelectItem>
                    <SelectItem value="10">Show last 10 notifications</SelectItem>
                    <SelectItem value="20">Show last 20 notifications</SelectItem>
                    <SelectItem value="50">Show last 50 notifications</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Choose how many notifications to keep in your history. Set to "Off" to disable all notifications entirely.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSaveSettings}
              disabled={loading}
              className="flex-1"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;